import { Search, SquarePen, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useLocation } from "react-router";
import { Input } from "src/components/ui/input";
import { useMe } from "src/context/MeContext";
import { usePusherEvent } from "src/hooks/use-pusher-event";
import { usePusherMultiEvent } from "src/hooks/use-pusher-multi-event";
import { isRootlyBranded } from "src/utils/brandTheme";

import { useConversationCategories } from "../hooks/useConversationCategories";
import type { LlmConversationSidebar_me$key } from "./__generated__/LlmConversationSidebar_me.graphql";
import ConversationItem from "./ConversationItem";

interface LlmConversationSidebarProps {
  me: LlmConversationSidebar_me$key;
  conversationId?: string;
  onCreateNewConversation: () => void;
  onSelectConversation: (id: string) => void;
  onDeleteConversation?: (id: string, title: string, e: React.MouseEvent) => void;
  onRenameConversation?: (id: string, newTitle: string) => void;
  onRefetch?: () => void;
  markedReadIds?: Set<string>;
}

const fragment = graphql`
  fragment LlmConversationSidebar_me on GraphQLUser {
    llmConversations(sources: [UI]) {
      id
      source
      sourceId
      createdAt
      title
      hasActiveInteraction
      hasUnreadResponse
      lastUserMessageTime
    }
  }
`;

const LlmConversationSidebar: React.FC<LlmConversationSidebarProps> = ({
  me,
  conversationId,
  onCreateNewConversation,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation,
  onRefetch,
  markedReadIds,
}) => {
  const meData = useFragment(fragment, me);

  const location = useLocation();
  const isOnNewChatPage = location.pathname === "/chat" || !conversationId;
  const meUser = useMe();
  const brandTheme = meUser.organization?.brandTheme ?? "TIERZERO";
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Transform LlmConversations to match Conversation structure for categorization
  const conversations = useMemo(() => {
    const llmConvs = meData?.llmConversations || [];
    return llmConvs.map((conv) => {
      // Use the computed title from the backend
      const title = conv.title || conv.sourceId || conv.id;

      return {
        id: conv.id,
        title,
        // Use lastUserMessageTime for ordering (stable during agent responses), fallback to createdAt
        updatedAt: conv.lastUserMessageTime || conv.createdAt,
        createdAt: conv.createdAt,
        source: conv.source,
        hasActiveInteraction: conv.hasActiveInteraction,
        hasUnreadResponse: conv.hasUnreadResponse && !markedReadIds?.has(conv.id),
      };
    });
  }, [meData?.llmConversations, markedReadIds]);

  // Get IDs of conversations with active interactions or unread responses for Pusher subscriptions
  const subscribedConversationIds = useMemo(() => {
    return conversations
      .filter((c) => c.hasActiveInteraction || c.hasUnreadResponse)
      .map((c) => c.id);
  }, [conversations]);

  // Subscribe to Pusher events for all active conversations to update streaming status
  const handlePusherEvent = useCallback(() => {
    onRefetch?.();
  }, [onRefetch]);

  usePusherMultiEvent({
    channelIds: subscribedConversationIds,
    eventType: "LLM_CONVERSATION_UPDATED",
    onEvent: handlePusherEvent,
    enabled: subscribedConversationIds.length > 0 && !!onRefetch,
  });

  // Listen for AI-generated title updates on the organization channel
  const organizationId = meUser.organization?.id;
  usePusherEvent<{ llm_conversation_id: string; title: string }>({
    channelId: organizationId ? `organization-${organizationId}` : undefined,
    eventType: "LLM_CONVERSATION_TITLE_UPDATED",
    onEvent: useCallback(() => {
      onRefetch?.();
    }, [onRefetch]),
  });

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) {
      return conversations;
    }

    return conversations.filter((conversation) =>
      conversation.title.toLowerCase().includes(normalizedSearch),
    );
  }, [conversations, searchTerm]);

  // Use the custom hook to categorize conversations
  const { todayConversations, yesterdayConversations, previous7DaysConversations, olderConversations } =
    useConversationCategories(filteredConversations);

  // Render a conversation item
  const renderConversationItem = (conversation: any) => {
    const handleDelete = onDeleteConversation
      ? (id: string, e: React.MouseEvent) => onDeleteConversation(id, conversation.title, e)
      : (() => {}) as any; // No-op if not provided

    return (
      <ConversationItem
        key={conversation.id}
        conversation={conversation}
        isSelected={conversationId === conversation.id}
        isStreaming={conversation.hasActiveInteraction}
        hasUnreadResponse={conversation.hasUnreadResponse}
        onDelete={handleDelete}
        onSelect={onSelectConversation}
        onRename={onRenameConversation || ((() => {}) as any)}
      />
    );
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        <nav className="space-y-2 mb-0.5">
          <button
            type="button"
            onClick={onCreateNewConversation}
            className={`flex h-9 w-full items-center justify-start gap-2 rounded-md border-0 px-3 py-1 text-[13px] transition-colors ${
              isOnNewChatPage
                ? "bg-foreground/10 text-foreground"
                : "text-secondary-foreground hover:bg-foreground/10 hover:text-foreground"
            }`}
          >
            <SquarePen size={14} />
            New chat
          </button>

          {conversations.length > 0 && (
            <>
              {isSearchOpen ? (
                <div className="relative">
                  <Input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search chats"
                    className="h-9 w-[calc(100%-1px)] ml-[0.5px] pr-7 text-xs bg-input border-border placeholder:text-muted-foreground"
                    autoFocus
                  />
                  {searchTerm.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                      className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsSearchOpen(true)}
                  className="flex h-9 w-full items-center justify-start gap-2 rounded-md border-0 px-3 py-1 text-[13px] text-secondary-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
                >
                  <Search size={14} />
                  Search chats
                </button>
              )}
            </>
          )}
        </nav>

        {conversations.length === 0 ? (
          <div className="p-6 text-[13px] text-muted-foreground text-center">
            {isRootlyBranded(brandTheme) ? "Ask a question to get started." : "Ask TierZero a question to get started."}
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="p-6 text-[13px] text-muted-foreground text-center">No chats match your search.</div>
        ) : (
          <nav className="space-y-1 w-full">
            {/* Today's conversations */}
            {todayConversations.length > 0 && (
              <div>
                <div className="pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider mb-1">Today</div>
                <div className="space-y-1 w-full">{todayConversations.map(renderConversationItem)}</div>
              </div>
            )}

            {/* Yesterday's conversations */}
            {yesterdayConversations.length > 0 && (
              <div>
                <div className="pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider mb-1">Yesterday</div>
                <div className="space-y-1 w-full">{yesterdayConversations.map(renderConversationItem)}</div>
              </div>
            )}

            {/* Previous 7 Days conversations */}
            {previous7DaysConversations.length > 0 && (
              <div>
                <div className="pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider mb-1">Previous 7 Days</div>
                <div className="space-y-1 w-full">{previous7DaysConversations.map(renderConversationItem)}</div>
              </div>
            )}

            {/* Older conversations */}
            {olderConversations.length > 0 && (
              <div>
                <div className="pt-3 pb-1 text-xs text-muted-foreground uppercase tracking-wider mb-1">History</div>
                <div className="space-y-1 w-full">{olderConversations.map(renderConversationItem)}</div>
              </div>
            )}
          </nav>
        )}
      </div>
    </div>
  );
};

export default LlmConversationSidebar;
